import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faKeyboard,
  faTimes,
  faQuestionCircle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

// Define keyboard shortcuts that are available globally
const GLOBAL_SHORTCUTS = [
  { key: "Alt + E", description: "Edit selected row" },
  { key: "Alt + S", description: "Save current edits" },
  { key: "Alt + C", description: "Cancel current edits" },
  { key: "Alt + F", description: "Focus search box" },
  { key: "Alt + R", description: "Refresh data" },
  { key: "Alt + X", description: "Export data" },
  { key: "/", description: "Search" },
  { key: "?", description: "Show this help" },
  { key: "Esc", description: "Close dialogs/panels" },
];

// Define keyboard shortcuts for editing dates
const DATE_SHORTCUTS = [
  { key: "T", description: "Today" },
  { key: "Y", description: "Yesterday" },
  { key: "W", description: "Last week" },
  { key: "N", description: "Now (current time)" },
  { key: "C", description: "Clear value" },
  { key: "Enter", description: "Save" },
  { key: "Esc", description: "Cancel" },
];

const KeyboardShortcutsHelper = ({
  onToggle,
  isOpen = false,
  onShortcutAction = () => {},
}) => {
  const [showHelp, setShowHelp] = useState(isOpen);

  // Handle keyboard events globally
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Show shortcuts panel when ? is pressed
      if (e.key === "?" && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        setShowHelp((prev) => !prev);
        if (onToggle) onToggle(!showHelp);
      }

      // Handle Alt+key shortcuts
      if (e.altKey) {
        switch (e.key.toUpperCase()) {
          case "E": // Edit
            e.preventDefault();
            onShortcutAction("edit");
            break;
          case "S": // Save
            e.preventDefault();
            onShortcutAction("save");
            break;
          case "C": // Cancel
            e.preventDefault();
            onShortcutAction("cancel");
            break;
          case "F": // Focus search
            e.preventDefault();
            onShortcutAction("focusSearch");
            break;
          case "R": // Refresh
            e.preventDefault();
            onShortcutAction("refresh");
            break;
          case "X": // Export
            e.preventDefault();
            onShortcutAction("export");
            break;
          default:
            break;
        }
      }

      // Handle / for search
      if (e.key === "/" && !e.ctrlKey && !e.altKey && !e.metaKey) {
        // Only if we're not already in an input element
        if (e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA") {
          e.preventDefault();
          onShortcutAction("focusSearch");
        }
      }

      // Handle Escape key
      if (e.key === "Escape") {
        if (showHelp) {
          setShowHelp(false);
          if (onToggle) onToggle(false);
        } else {
          // Let the parent component handle other Escape actions
          onShortcutAction("escape");
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showHelp, onShortcutAction, onToggle]);

  // Update local state when isOpen prop changes
  useEffect(() => {
    setShowHelp(isOpen);
  }, [isOpen]);

  // Helper button that shows and reveals shortcuts panel
  const ShortcutsButton = () => (
    <button
      className="fixed bottom-4 right-4 p-3 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-colors duration-200"
      onClick={() => {
        setShowHelp((prev) => !prev);
        if (onToggle) onToggle(!showHelp);
      }}
      title="Keyboard Shortcuts"
    >
      <FontAwesomeIcon icon={faKeyboard} />
    </button>
  );

  // The shortcuts help panel
  const ShortcutsPanel = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-700 to-blue-600 text-white rounded-t-lg">
          <h3 className="text-lg font-medium flex items-center">
            <FontAwesomeIcon icon={faKeyboard} className="mr-2" />
            Keyboard Shortcuts
          </h3>
          <button
            onClick={() => {
              setShowHelp(false);
              if (onToggle) onToggle(false);
            }}
            className="text-white hover:text-blue-200"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="mr-1 text-blue-500"
              />
              Global Shortcuts
            </h4>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-2">
                {GLOBAL_SHORTCUTS.map((shortcut, index) => (
                  <div key={index} className="flex items-center">
                    <span className="px-2 py-0.5 bg-gray-200 text-gray-800 rounded text-xs font-mono mr-2">
                      {shortcut.key}
                    </span>
                    <span className="text-sm text-gray-700">
                      {shortcut.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="mr-1 text-blue-500"
              />
              Date Editing Shortcuts
            </h4>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-2">
                {DATE_SHORTCUTS.map((shortcut, index) => (
                  <div key={index} className="flex items-center">
                    <span className="px-2 py-0.5 bg-gray-200 text-gray-800 rounded text-xs font-mono mr-2">
                      {shortcut.key}
                    </span>
                    <span className="text-sm text-gray-700">
                      {shortcut.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-2">
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="mr-1 text-blue-500"
              />
              Tips
            </h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-blue-500 mt-0.5 mr-2">•</span>
                <span>Double-click on any date cell to quickly edit it</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mt-0.5 mr-2">•</span>
                <span>
                  Press{" "}
                  <code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">
                    Tab
                  </code>{" "}
                  to move between editable fields
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mt-0.5 mr-2">•</span>
                <span>
                  Press{" "}
                  <code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">
                    ?
                  </code>{" "}
                  anywhere to show/hide this help panel
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div className="px-6 py-3 bg-gray-50 rounded-b-lg">
          <button
            onClick={() => {
              setShowHelp(false);
              if (onToggle) onToggle(false);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Always render the button for quick access */}
      <ShortcutsButton />

      {/* Render the help panel conditionally */}
      {showHelp && <ShortcutsPanel />}
    </>
  );
};

export default KeyboardShortcutsHelper;
